import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";

import {
  addNewContractorEmail,
  addVindicationSetting,
  editContractorEmailAddress,
  editVindicationSetting,
  getContractorEmailList,
  getAutoSendMailConfiguration,
  getVindicationSetting,
  getXmlGenerators,
  removeContractorEmailAddress,
  updateAutoSendMailConfiguration,
  updateInvoiceSpecification,
  deleteInvoiceSpecification,
  getSpecificationColumns,
  getBookingNotification,
  updateBookingNotification,
} from "@/requests/administration/contractors/contractorMailNotification";
import { AddEditEmailAddress, EmailAddress } from "@/models/api/administration/contractors/EmailAddress";
import {
  AutoSendMailConfigurationModel,
  EditInvoiceSpecification,
  InvoiceSpecification,
  SpecificationColumn,
} from "@/models/api/administration/contractors/AutoSendMailConfigurationModel";
import { getMailBoxes } from "@/requests/mailBox";
import { MAIL_TYPE_ENUM } from "@/static/mailTypeEnum";
import { MailBox } from "@/models/api/mail/NotificationData";
import { SelectItem } from "@/models/api/SelectItem";
import {
  AddEditVindicationSetting,
  VindicationSetting,
} from "@/models/api/administration/contractors/VindicationSetting";
import {
  BookingNotificationConfiguration,
  EditBookingNotificationConfiguration,
} from "@/models/api/administration/contractors/BookingNotificationConfiguration";

export interface AdministrationContractorMailNotificationInterface {
  isAddNewDialog: boolean;
  isEditDialog: boolean;
  contractorEmailId: number | null;
  contractorEmails: Array<EmailAddress>;
  contractorEmailModel: AddEditEmailAddress | null;
  vindicationSetting: VindicationSetting | null;

  mailBoxes: Array<MailBox>;
  xmlGeneratorList: Array<SelectItem>;
  autoSendMailConfiguration: AutoSendMailConfigurationModel;
  specificationColumns: Array<SpecificationColumn>;
  specification: InvoiceSpecification | null;

  bookingNotification: BookingNotificationConfiguration | null;
}

export const AdministrationContractorMailNotificationState: AdministrationContractorMailNotificationInterface = {
  isAddNewDialog: false,
  isEditDialog: false,
  contractorEmailId: null,
  contractorEmails: [],
  contractorEmailModel: null,
  vindicationSetting: null,

  mailBoxes: [],
  xmlGeneratorList: [],
  autoSendMailConfiguration: new AutoSendMailConfigurationModel(),
  specificationColumns: [],
  specification: null,

  bookingNotification: null,
};

const AdministrationContractorMailNotificationModule = defineModule({
  state: AdministrationContractorMailNotificationState as AdministrationContractorMailNotificationInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorEmails(state, param: Array<EmailAddress>) {
      state.contractorEmails = param;
    },
    setIsAddNewDialog(state, param: boolean) {
      state.isAddNewDialog = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setContractorEmailModel(state, param: EmailAddress) {
      state.contractorEmailModel = param as AddEditEmailAddress;
      state.contractorEmailId = param.id;
    },
    setVindicationSetting(state, param: VindicationSetting | null) {
      state.vindicationSetting = param;
    },

    setMailBoxes(state, param: Array<MailBox>) {
      state.mailBoxes = param;
    },
    setXmlGeneratorList(state, param: Array<SelectItem>) {
      state.xmlGeneratorList = param;
    },
    setAutoSendMailConfiguration(state, param: AutoSendMailConfigurationModel) {
      state.autoSendMailConfiguration = param;
    },
    setSpecificationColumns(state, param: Array<SpecificationColumn>) {
      state.specificationColumns = param;
    },
    setSpecification(state, param: InvoiceSpecification | null) {
      state.specification = param;
    },
    setBookingNotification(state, param: BookingNotificationConfiguration | null) {
      state.bookingNotification = param;
    },
  },
  actions: {
    async getContractorMailNotificationMailBoxes(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getMailBoxes(MAIL_TYPE_ENUM.NotificationConfiguration);
      commit.setMailBoxes(response);
    },
    async getContractorsEmailList(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const response = await getContractorEmailList(
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorEmails(response);
      }
    },
    async addNewContractorEmail(context, param: AddEditEmailAddress) {
      const { rootState, dispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        param.clearNotUsedAddress();
        await addNewContractorEmail(rootState.administration.contractors.list.selectedContractorId, param);
        await dispatch.getContractorsEmailList();
      }
    },
    async editContractorEmail(context, param: AddEditEmailAddress) {
      const { state, dispatch } = _moduleActionContext(context);
      param.clearNotUsedAddress();
      await editContractorEmailAddress(state.contractorEmailId as number, param);
      await dispatch.getContractorsEmailList();
    },
    async removeContractorEmail(context, contractorEmailId: number) {
      const { dispatch } = _moduleActionContext(context);
      await removeContractorEmailAddress(contractorEmailId);
      await dispatch.getContractorsEmailList();
    },
    async getXmlFileGenerators(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getXmlGenerators();
      response.push({
        text: "Empty",
        value: null,
      } as SelectItem);
      commit.setXmlGeneratorList(response);
    },
    async getAutoSendMailConfiguration(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId) {
        const response = await getAutoSendMailConfiguration(
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setAutoSendMailConfiguration(response);
      }
    },
    async updateAutoSendMailConfiguration(context) {
      const { state, rootState, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId) {
        await updateAutoSendMailConfiguration(
          rootState.administration.contractors.list.selectedContractorId,
          state.autoSendMailConfiguration
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async getSpecificationColumns(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getSpecificationColumns();
      commit.setSpecificationColumns(response);
    },
    async updateInvoiceSpecification(context, param: EditInvoiceSpecification) {
      const { rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId)
        await updateInvoiceSpecification(rootState.administration.contractors.list.selectedContractorId, param);
    },
    async deleteInvoiceSpecification(context, documentTypeId: number | null) {
      const { rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId)
        await deleteInvoiceSpecification(
          rootState.administration.contractors.list.selectedContractorId,
          documentTypeId
        );
    },
    async getVindicationSetting(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const response = await getVindicationSetting(rootState.administration.contractors.list.selectedContractorId);
        commit.setVindicationSetting(response);
      }
    },
    async addVindicationSetting(context, model: AddEditVindicationSetting) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addVindicationSetting(rootState.administration.contractors.list.selectedContractorId, model);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async editVindicationSetting(
      context,
      model: {
        vindicationSettingId: number;
        body: AddEditVindicationSetting;
      }
    ) {
      const { rootDispatch } = _moduleActionContext(context);
      await editVindicationSetting(model.vindicationSettingId, model.body);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async getBookingNotification(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const response = await getBookingNotification(rootState.administration.contractors.list.selectedContractorId);
        commit.setBookingNotification(response);
      }
    },
    async updateBookingNotification(context) {
      const { state, rootState, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId) {
        await updateBookingNotification(
          rootState.administration.contractors.list.selectedContractorId,
          state.bookingNotification as EditBookingNotificationConfiguration
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
  },
});

export default AdministrationContractorMailNotificationModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorMailNotificationModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorMailNotificationModule);
